/**
 * App WebView / 手机端可靠跳转（hash 路由失败时强制改 hash）
 */
import { ElMessage } from 'element-plus'

let lockUntil = 0

export function toHash(target) {
  if (typeof target === 'string') {
    const p = target.startsWith('/') ? target : `/${target}`
    return `#${p}`
  }
  const path = String(target?.path || '/').startsWith('/')
    ? String(target.path)
    : `/${target?.path || ''}`
  const q = target?.query && typeof target.query === 'object' ? target.query : null
  let qs = ''
  if (q) {
    const sp = new URLSearchParams()
    Object.keys(q).forEach((k) => {
      if (q[k] == null || q[k] === '') return
      sp.set(k, String(q[k]))
    })
    const s = sp.toString()
    if (s) qs = `?${s}`
  }
  return `#${path}${qs}`
}

function sameHash(a, b) {
  const na = String(a || '').replace(/^#/, '')
  const nb = String(b || '').replace(/^#/, '')
  return na === nb
}

function forceHash(hash) {
  try {
    if (!sameHash(window.location.hash, hash)) {
      window.location.hash = hash
    }
    setTimeout(() => {
      if (!sameHash(window.location.hash, hash)) {
        const base = `${window.location.pathname}${window.location.search || ''}`
        window.location.assign(`${base}${hash}`)
      }
    }, 100)
    return true
  } catch (_) {
    ElMessage.error('打开页面失败，请重试')
    return false
  }
}

/** 是否在员工 App 的 H5 WebView 壳内（非电脑浏览器） */
export function isStaffAppShell() {
  if (typeof window === 'undefined') return false
  try {
    const q = new URLSearchParams(window.location.search || '')
    if (q.get('app') === '1' || q.get('shell') === 'none') return true
    const nat = window.__JF_NATIVE__
    return !!(nat && (nat.goWorkbench || nat.openPick || nat.openPrinter || nat.openMine))
  } catch (_) {
    return false
  }
}

function fireNativeBridge(action) {
  try {
    const msg = {
      v: 15,
      id: `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      action: String(action || '')
    }
    const enc = btoa(unescape(encodeURIComponent(JSON.stringify(msg))))
    const marker = `JF13:${enc}`
    document.title = '优选工作台'
    setTimeout(() => {
      try {
        document.title = marker
      } catch (_) {}
    }, 0)
    setTimeout(() => {
      try {
        if (document.title === marker) document.title = '优选工作台'
      } catch (_) {}
    }, 160)
    if (window.uni && typeof window.uni.postMessage === 'function') {
      window.uni.postMessage({ data: { jf: 1, enc } })
    } else if (window.UniAppJSBridge && window.uni && window.uni.webView && window.uni.webView.postMessage) {
      window.uni.webView.postMessage({ data: { jf: 1, enc } })
    }
    return true
  } catch (_) {
    return false
  }
}

/** App 内：回到原生方形工作台（不进入 H5 /portal） */
export function requestNativeWorkbench() {
  if (!isStaffAppShell()) return false
  try {
    const nat = window.__JF_NATIVE__
    if (nat && typeof nat.goWorkbench === 'function') {
      nat.goWorkbench().catch(() => fireNativeBridge('goWorkbench'))
      return true
    }
    if (fireNativeBridge('goWorkbench')) return true
  } catch (_) {}
  try {
    ElMessage.warning('请点页面左上角「‹ 工作台」返回')
  } catch (_) {}
  return false
}

function portalPathOf(target) {
  if (typeof target === 'string') {
    const p = target.split('?')[0]
    return p.startsWith('/') ? p : `/${p}`
  }
  const p = String(target?.path || '/')
  return p.startsWith('/') ? p : `/${p}`
}

/** App 内：回到原生今日配送页（不进入 H5 /delivery） */
export function requestNativeDelivery() {
  if (!isStaffAppShell()) return false
  try {
    const nat = window.__JF_NATIVE__
    if (nat && typeof nat.openDelivery === 'function') {
      nat.openDelivery().catch(() => fireNativeBridge('openDelivery'))
      return true
    }
    if (fireNativeBridge('openDelivery')) return true
  } catch (_) {}
  return false
}

/** 回工作台：App 内回原生；浏览器进 H5 /portal */
export function goPortalReplace(router) {
  if (isStaffAppShell()) {
    requestNativeWorkbench()
    return true
  }
  try {
    router.replace('/portal')
    return true
  } catch (_) {
    return forceHash('#/portal')
  }
}

const FROM_BACK_MAP = {
  delivery: { path: '/delivery', label: '返回配送' },
  orders: { path: '/orders', label: '返回订单' },
  users: { path: '/users', label: '返回客户管理' },
  pick: { path: '/pick', label: '返回分检' },
  portal: { path: '/portal', label: '返回工作台' }
}

export function backMetaFromRoute(route) {
  const from = String(route?.query?.from || '').trim()
  return FROM_BACK_MAP[from] || null
}

/** 带来源参数时回到来源页，否则 replace 到工作台 */
export async function goBackSmart(router, route) {
  const from = String(route?.query?.from || '').trim()
  if (from === 'delivery' && isStaffAppShell()) {
    return goAppRoute(router, '/delivery')
  }
  const meta = backMetaFromRoute(route)
  if (meta) {
    if (meta.path === '/delivery' && isStaffAppShell()) {
      return goAppRoute(router, '/delivery')
    }
    return goAppRoute(router, meta.path)
  }
  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back()
    return true
  }
  return goPortalReplace(router)
}

/**
 * @param {import('vue-router').Router} router
 * @param {string|{ path: string, query?: object }} target
 */
export async function goAppRoute(router, target) {
  const path = portalPathOf(target)
  if (path === '/portal' && isStaffAppShell()) {
    return goPortalReplace(router)
  }
  if (path === '/delivery' && isStaffAppShell()) {
    return requestNativeDelivery()
  }

  const now = Date.now()
  if (now < lockUntil) return false
  lockUntil = now + 450

  const hash = toHash(target)

  try {
    await Promise.race([
      router.push(target),
      new Promise((_, reject) => {
        setTimeout(() => reject(Object.assign(new Error('NAV_TIMEOUT'), { name: 'NavTimeout' })), 2200)
      })
    ])
  } catch (e) {
    const name = String(e?.name || '')
    if (name === 'NavigationDuplicated') {
      return true
    }
    // 超时 / 被拦 / WebView 异常：强制改 hash
    return forceHash(hash)
  }

  // push「成功」但页面没变（部分 WebView）
  await new Promise((r) => setTimeout(r, 50))
  if (!sameHash(window.location.hash, hash)) {
    return forceHash(hash)
  }
  return true
}
